import { RegisteredComponent } from "@builder.io/sdk-react";
import {
  CARD_FILTER_VALUE_FIELDS,
  DLD_FIELD,
  DLT_FIELD,
} from "../../commonFields";

export const indexCardsBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Index cards",
  canHaveChildren: false,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
    builderLinkComponent: true,
  },
  inputs: [
    {
      name: "sections",
      type: "list",
      subFields: [
        {
          name: "text",
          type: "string",
          localized: true,
          required: false,
        },
        {
          name: "cardFilter",
          type: "object",
          subFields: [
            ...(CARD_FILTER_VALUE_FIELDS ?? []),
            {
              name: "operand",
              type: "enum",
              enum: ["EQUAL", "NOT EQUAL"],
              showIf: (options) => options.get("type") !== "custom",
              localized: false,
              required: true,
              defaultValue: "EQUAL",
            },
            {
              name: "value",
              type: "string",
              showIf: (options) => options.get("type") !== "custom",
              localized: false,
              required: true,
            },
            {
              name: "callback",
              type: "javascript",
              showIf: (options) => options.get("type") === "custom",
              localized: false,
              required: false,
            },
          ],
          required: false,
        },
        {
          name: "layoutType",
          type: "enum",
          enum: ["masonry", "grid", "stack"],
          required: true,
          defaultValue: "grid",
        },
        {
          name: "numberOfColumns",
          type: "number",
          showIf: (options) => options.get("layoutType") === "masonry",
          enum: [
            {
              label: "2",
              value: 2,
            },
          ],
          defaultValue: 2,
        },
        {
          name: "numberOfColumns",
          type: "enum",
          showIf: (options) => options.get("layoutType") === "grid",
          enum: [
            {
              label: "2",
              value: 2,
            },
            {
              label: "3",
              value: 3,
            },
            {
              label: "4",
              value: 4,
            },
          ],
          defaultValue: {
            label: "4",
            value: 4,
          },
        },
        {
          name: "orderCardsByTitle",
          type: "longText",
          localized: false,
          required: false,
          helperText:
            "Works only for Product cards. Enter Product card titles separated by commas. Example: IP address manager, Access Rights Manager, patch manager, virtualization Manager.",
        },
      ],
    },
    {
      name: "paginationType",
      type: "object",
      subFields: [
        {
          name: "type",
          type: "enum",
          enum: ["infinite-scroll", "expandable-section"],
          localized: false,
          required: true,
          defaultValue: "expandable-section",
        },
        {
          name: "numberOfInitialItems",
          type: "number",
          localized: false,
          required: true,
          showIf: (options) => options.get("type") === "expandable-section",
          defaultValue: 12,
        },
        {
          name: "viewMoreText",
          type: "object",
          subFields: [
            {
              name: "text",
              type: "string",
              localized: true,
              required: true,
              defaultValue: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "View more",
              },
            },
            DLT_FIELD,
            DLD_FIELD,
          ],
          showIf: (options) => options.get("type") === "expandable-section",
        },
        {
          name: "viewLessText",
          type: "object",
          subFields: [
            {
              name: "text",
              type: "string",
              localized: true,
              required: true,
              defaultValue: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "View less",
              },
            },
            DLT_FIELD,
            DLD_FIELD,
          ],
          showIf: (options) => options.get("type") === "expandable-section",
        },
      ],
    },
    {
      name: "specificConfiguration",
      type: "object",
      subFields: [
        {
          name: "type",
          type: "enum",
          enum: ["Event"],
          localized: false,
          required: false,
        },
        {
          name: "fomoText",
          type: "object",
          localized: false,
          required: false,
          showIf: (options) => options.get("type") === "Event",
          subFields: [
            {
              name: "upcomingText",
              type: "string",
              localized: true,
              required: false,
              helperText:
                "This text is used when the event takes place within 7 days.",
            },
            {
              name: "todayText",
              type: "string",
              localized: true,
              required: false,
              helperText: "This text is used when the event takes place today.",
            },
          ],
        },
        {
          name: "promotedCategories",
          type: "Tags",
          localized: true,
          required: false,
          showIf: (options) => options.get("type") === "Event",
        },
        {
          name: "maxPromotedCategories",
          type: "number",
          localized: false,
          required: false,
          showIf: (options) => options.get("type") === "Event",
          helperText:
            "Number of maximum displayed categories at the top of the Event card.",
        },
      ],
    },
    {
      name: "notFound",
      type: "object",
      subFields: [
        {
          name: "isDisplayed",
          type: "boolean",
          localized: false,
          required: true,
        },
        {
          name: "message",
          type: "string",
          localized: true,
          required: true,
          helperText:
            "The message usually shows at the top. The {keyword} placeholder can be used, example usage: We couldn't find {keyword} use cases related to...",
          defaultValue: {
            "@type": "@builder.io/core:LocalizedValue",
            Default:
              "We couldn't find {keyword} use cases related to SolarWinds Observability SaaS.",
          },
          showIf: (options) => options.get("isDisplayed") === true,
        },
        {
          name: "suggestion",
          type: "richText",
          localized: true,
          required: true,
          defaultValue: {
            "@type": "@builder.io/core:LocalizedValue",
            Default:
              "<p><strong>Consider trying:</strong></p><ul><li>A rephrased search entry</li><li>Your search in another product area</li></ul>",
          },
          showIf: (options) => options.get("isDisplayed") === true,
        },
      ],
      required: true,
    },
  ],
};
